import { type AuthUser } from "wasp/auth";
import {
  useQuery,
  getAdminWorkflowSummary,
  getArticleWorkflowQueue,
} from "wasp/client/operations";
import { Link } from "wasp/client/router";
import { SurfaceCard } from "../../../components/ui";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import { useRedirectHomeUnlessUserIsAdmin } from "../../useRedirectHomeUnlessUserIsAdmin";

function HealthCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "warning" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "text-danger-600"
      : tone === "warning"
        ? "text-warning-600"
        : "text-black dark:text-white";
  return (
    <SurfaceCard padding="sm">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </SurfaceCard>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <SurfaceCard padding="sm">
      <p className="text-sm text-neutral-600 dark:text-neutral-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-black dark:text-white">
        {value}
      </p>
    </SurfaceCard>
  );
}

const getSentimentColor = (sentiment: number) => {
  if (sentiment >= 4) return "text-success-600";
  if (sentiment >= 2) return "text-warning-600";
  return "text-danger-600";
};

const OperationsOverviewPage = ({ user }: { user: AuthUser }) => {
  useRedirectHomeUnlessUserIsAdmin({ user });
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery(getAdminWorkflowSummary);
  const { data: recentArticles, isLoading: articlesLoading } = useQuery(
    getArticleWorkflowQueue,
    { skip: 0, limit: 5 },
  );

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName="Dashboard" />
      <div className="flex flex-col gap-6">
        {summaryError && (
          <SurfaceCard>
            <p className="text-danger-600">
              Failed to load dashboard: {summaryError.message}
            </p>
          </SurfaceCard>
        )}

        {summaryLoading && (
          <SurfaceCard>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Loading dashboard...
            </p>
          </SurfaceCard>
        )}

        {summary && (
          <>
            {/* Health indicators */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                Health
              </h3>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
                <HealthCard
                  label="Degraded feeds"
                  value={summary.queueCounts.degradedFeeds}
                  tone={
                    summary.queueCounts.degradedFeeds > 0 ? "danger" : "neutral"
                  }
                />
                <HealthCard
                  label="Failed deliveries"
                  value={summary.queueCounts.failedDeliveries}
                  tone={
                    summary.queueCounts.failedDeliveries > 0
                      ? "danger"
                      : "neutral"
                  }
                />
                <HealthCard
                  label="Pending reports"
                  value={summary.queueCounts.pendingReports}
                  tone={
                    summary.queueCounts.pendingReports > 0
                      ? "warning"
                      : "neutral"
                  }
                />
                <HealthCard
                  label="Extraction queue"
                  value={summary.queueCounts.extractionAttention}
                  tone={
                    summary.queueCounts.extractionAttention > 5
                      ? "warning"
                      : "neutral"
                  }
                />
                <HealthCard
                  label="Error logs"
                  value={summary.healthSignals.highSeverityLogs}
                  tone={
                    summary.healthSignals.highSeverityLogs > 0
                      ? "warning"
                      : "neutral"
                  }
                />
                <HealthCard
                  label="Flagged articles"
                  value={summary.queueCounts.flaggedArticles}
                  tone={
                    summary.queueCounts.flaggedArticles > 0
                      ? "warning"
                      : "neutral"
                  }
                />
              </div>
            </div>

            {/* Quick stats */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                Overview
              </h3>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <StatCard
                  label="Active feeds"
                  value={summary.healthSignals.activeFeeds}
                />
                <Link to={"/admin/messages" as any} className="block">
                  <SurfaceCard
                    padding="sm"
                    className={
                      summary.queueCounts.unreadMessages > 0
                        ? "border-warning/50"
                        : ""
                    }
                  >
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Unread messages
                    </p>
                    <p
                      className={`mt-1 text-2xl font-semibold ${
                        summary.queueCounts.unreadMessages > 0
                          ? "text-warning-600"
                          : "text-black dark:text-white"
                      }`}
                    >
                      {summary.queueCounts.unreadMessages}
                    </p>
                  </SurfaceCard>
                </Link>
                <Link to={"/admin/analytics" as any} className="block">
                  <SurfaceCard padding="sm">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Analytics
                    </p>
                    <p className="mt-1 text-sm text-info">
                      View revenue &amp; traffic
                    </p>
                  </SurfaceCard>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Recent articles */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-black dark:text-white">
              Recent articles
            </h3>
            <Link
              to={"/admin/articles" as any}
              className="text-xs text-info hover:text-info-800 dark:text-info-400"
            >
              View all
            </Link>
          </div>
          <SurfaceCard padding="none">
            {articlesLoading && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Loading articles...
                </p>
              </div>
            )}
            {recentArticles?.articles && recentArticles.articles.length > 0
              ? recentArticles.articles.map((article: any) => (
                  <div
                    key={article.id}
                    className="flex items-center justify-between gap-4 border-t border-stroke px-4 py-3 first:border-t-0 dark:border-strokedark"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-black dark:text-white">
                        {article.title}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {article.provider} &middot;{" "}
                        {new Date(article.publishedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-sm font-semibold ${getSentimentColor(
                        article.sentiment,
                      )}`}
                    >
                      {article.sentiment}
                    </span>
                  </div>
                ))
              : !articlesLoading && (
                  <div className="px-4 py-6 text-center">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      No articles found.
                    </p>
                  </div>
                )}
          </SurfaceCard>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default OperationsOverviewPage;
